import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    listbudgets,
    createbudget,
    updatebudget,
    deletebudget,
    listaccounts,
    listfinancialyears,
    listbranch
} from "../../../../services/admin";
import { toast } from "react-toastify";
import moment from "moment";
import Swal from "sweetalert2";

export default function AllBudgets() {
    const [budgets, setBudgets] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);
    const [formData, setFormData] = useState({
        financial_year_id: "",
        account_id: "",
        branch_id: localStorage.getItem("branch") || "",
        period_type: "Annual",
        period_name: "",
        start_date: "",
        end_date: "",
        budgeted_amount: "",
        warning_threshold: "80",
        alert_threshold: "100",
        is_active: true,
        remarks: ""
    });

    // Filters
    const [filters, setFilters] = useState({
        financial_year_id: "",
        period_type: "",
        status: ""
    });

    const getUtilizationColor = (utilization) => {
        if (utilization >= 100) return 'danger';
        if (utilization >= 80) return 'warning';
        return 'success';
    };

    const getStatusBadgeText = (status) => {
        if (status === 'Good') return <span className="badge bg-success">Good</span>;
        if (status === 'Warning') return <span className="badge bg-warning">Warning</span>;
        return <span className="badge bg-danger">Alert</span>;
    };

    const columnDefs = [
        {
            headerName: "Account",
            field: "account.account_name",
            width: 200,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                const account = params.data.account;
                if (!account) return '-';
                return (
                    <div>
                        <strong>{account.account_name}</strong>
                        <br />
                        <small className="text-muted">
                            {account.account_code}
                        </small>
                    </div>
                );
            }
        },
        {
            headerName: "Branch",
            field: "branch.branch_name",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            valueGetter: function (params) {
                return params.data.branch?.branch_name || 'All Branches';
            }
        },
        {
            headerName: "Period",
            field: "period_type",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                return (
                    <div>
                        <span className="badge bg-info">
                            {params.data?.period_type}
                        </span>

                        {params.data?.period_name && (
                            <div>
                                <small>{params.data.period_name}</small>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            headerName: "Start - End Date",
            field: "start_date",
            width: 170,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <div>
                    <div>{moment(params.data.start_date).format('DD-MM-YYYY')}</div>
                    <div>{moment(params.data.end_date).format('DD-MM-YYYY')}</div>
                </div>
            )
        },
        {
            headerName: "Budgeted",
            field: "budgeted_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return <strong>₹ {parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>;
            }
        },
        {
            headerName: "Actual",
            field: "actual_spending",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return `₹ ${parseFloat(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
            }
        },
        {
            headerName: "Variance",
            field: "variance",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: function (params) {
                return {
                    textAlign: "right",
                    color: params.value >= 0 ? 'green' : 'red'
                };
            },
            cellRenderer: function (params) {
                return `₹ ${parseFloat(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
            }
        },
        {
            headerName: "Utilization",
            field: "utilization_percentage",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const utilization = parseFloat(params.value || 0);
                const color = getUtilizationColor(utilization);
                return (
                    <div className="d-flex align-items-center" style={{ height: "100%" }}>
                        <div className="progress flex-grow-1 me-2" style={{ height: "16px", minWidth: "55px" }}>
                            <div
                                className={`progress-bar bg-${color}`}
                                role="progressbar"
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                                aria-valuenow={utilization}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                        <small className="fw-bold text-dark" style={{ whiteSpace: "nowrap" }}>
                            {utilization.toFixed(1)}%
                        </small>
                    </div>
                );
            }
        },
        {
            headerName: "Status",
            field: "status",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return getStatusBadgeText(params.value);
            }
        },
        {
            headerName: "Actions",
            field: "id",
            width: 150,
            sortable: false,
            resizable: true,
            filter: false,
            floatingFilter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return (
                    <>
                        <button className="btn btn-sm btn-primary me-1" onClick={() => window.handleEditBudget(params.value)} title="Edit Budget">
                            <i className="fa fa-edit"></i>
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => window.handleDeleteBudget(params.value)} title="Delete Budget">
                            <i className="fa fa-trash"></i>
                        </button>
                    </>
                )   ;
            }
        }
    ];

    // Load once on mount. This must not depend on `budgets`: fetchBudgets()
    // calls setBudgets(), so re-running on every change would refetch forever
    // and hammer the API with the same four requests.
    useEffect(() => {
        fetchBudgets();
        fetchAccounts();
        fetchFinancialYears();
        fetchBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The AG Grid action buttons call these through window, so they need to see
    // the current budgets — this part does belong on [budgets].
    useEffect(() => {
        window.handleEditBudget = (id) => {
            const budget = budgets.find(b => b.id === id);
            if (budget) handleEdit(budget);
        };
        window.handleDeleteBudget = (id) => {
            const budget = budgets.find(b => b.id === id);
            if (budget) handleDelete(budget);
        };

        return () => {
            delete window.handleEditBudget;
            delete window.handleDeleteBudget;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgets]);

    const fetchBudgets = (filterParams = {}) => {
        setLoading(true);
        const params = { ...filters, ...filterParams };
        listbudgets(params).then((res) => {
            if (res.status === true) {
                setBudgets(Array.isArray(res.data) ? res.data : []);
            } else {
                setBudgets([]);
            }
            setLoading(false);
        }).catch(() => {
            setBudgets([]);
            setLoading(false);
        });
    };

    const fetchAccounts = () => {
        listaccounts(localStorage.getItem("branch") || "").then((res) => {
            if (res.status === true) {
                setAccounts(res.data.details || []);
            }
        }).catch(() => {
            setAccounts([]);
        });
    };

    const fetchFinancialYears = () => {
        listfinancialyears().then((res) => {
            if (res.status === true) {
                setFinancialYears(Array.isArray(res.data) ? res.data : []);
            }
        }).catch(() => {
            setFinancialYears([]);
        });
    };

    const fetchBranches = () => {
        listbranch(1).then((res) => {
            if (res.status === true) {
                setBranches(Array.isArray(res.data) ? res.data : []);
            }
        }).catch(() => {
            setBranches([]);
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const newFilters = {
            ...filters,
            [name]: value
        };
        setFilters(newFilters);
        fetchBudgets(newFilters);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.financial_year_id || !formData.account_id || !formData.start_date || !formData.end_date || !formData.budgeted_amount) {
            toast.error("Please fill all required fields");
            return;
        }

        if (editingBudget) {
            // Update
            updatebudget(editingBudget.id, formData).then((res) => {
                if (res.status === true) {
                    toast.success("Budget Updated Successfully");
                    fetchBudgets();
                    resetForm();
                } else {
                    toast.error(res.message || "Failed to update");
                }
            });
        } else {
            // Create
            createbudget(formData).then((res) => {
                if (res.status === true) {
                    toast.success("Budget Created Successfully");
                    fetchBudgets();
                    resetForm();
                } else {
                    toast.error(res.message || "Failed to create");
                }
            });
        }
    };

    const handleEdit = (budget) => {
        setEditingBudget(budget);
        setFormData({
            financial_year_id: budget.financial_year_id,
            account_id: budget.account_id,
            branch_id: budget.branch_id || localStorage.getItem("branch") || "",
            period_type: budget.period_type,
            period_name: budget.period_name || "",
            start_date: moment(budget.start_date).format('YYYY-MM-DD'),
            end_date: moment(budget.end_date).format('YYYY-MM-DD'),
            budgeted_amount: budget.budgeted_amount,
            warning_threshold: budget.warning_threshold,
            alert_threshold: budget.alert_threshold,
            is_active: budget.is_active,
            remarks: budget.remarks || ""
        });
        setShowForm(true);
    };

    const handleDelete = (budget) => {
        Swal.fire({
            title: 'Delete Budget?',
            text: `Delete budget for ${budget.account?.account_name}? This cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deletebudget(budget.id).then((res) => {
                    if (res.status === true) {
                        toast.success("Budget Deleted Successfully");
                        fetchBudgets();
                    } else {
                        toast.error(res.message || "Failed to delete");
                    }
                });
            }
        });
    };

    const resetForm = () => {
        const defaultBranchId = localStorage.getItem("branch") || "";
        setFormData({
            financial_year_id: "",
            account_id: "",
            branch_id: defaultBranchId,
            period_type: "Annual",
            period_name: "",
            start_date: "",
            end_date: "",
            budgeted_amount: "",
            warning_threshold: "80",
            alert_threshold: "100",
            is_active: true,
            remarks: ""
        });
        setEditingBudget(null);
        setShowForm(false);
    };

    // Get branch name for display
    const getBranchName = () => {
        if (!formData.branch_id) return "All Branches";
        const branch = branches.find(b => b.id == formData.branch_id);
        return branch ? branch.branch_name : "All Branches";
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Budget Management</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Budget
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Budget Management</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {!showForm && (
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => setShowForm(true)}
                                                >
                                                    <i className="fa fa-plus"></i> Create New Budget
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {/* Filters */}
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <label className="form-label">Financial Year</label>
                                                    <select
                                                        className="default-select  form-control wide"
                                                        name="financial_year_id"
                                                        value={filters.financial_year_id}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Years</option>
                                                        {financialYears.map((year) => (
                                                            <option key={year.id} value={year.id}>
                                                                {year.year_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Period Type</label>
                                                    <select
                                                        className="default-select  form-control wide"
                                                        name="period_type"
                                                        value={filters.period_type}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Types</option>
                                                        <option value="Annual">Annual</option>
                                                        <option value="Quarterly">Quarterly</option>
                                                        <option value="Monthly">Monthly</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Status</label>
                                                    <select
                                                        className="default-select  form-control wide"
                                                        name="status"
                                                        value={filters.status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="active">Active Only</option>
                                                        <option value="current">Current Period</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">&nbsp;</label>
                                                    <button
                                                        className="btn btn-secondary w-100"
                                                        onClick={() => {
                                                            setFilters({ financial_year_id: "", period_type: "", status: "" });
                                                            fetchBudgets({});
                                                        }}
                                                    >
                                                        Clear Filters
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {showForm && (
                                        <div className="card mb-4">
                                            <div className="card-header bg-light">
                                                <h5>{editingBudget ? 'Edit' : 'Create'} Budget</h5>
                                            </div>
                                            <div className="card-body">
                                                <form onSubmit={handleSubmit}>
                                                    <div className="row">
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">
                                                                Financial Year <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="default-select  form-control wide"
                                                                name="financial_year_id"
                                                                value={formData.financial_year_id}
                                                                onChange={handleInputChange}
                                                                required
                                                            >
                                                                <option value="">Select Financial Year</option>
                                                                {financialYears.map((year) => (
                                                                    <option key={year.id} value={year.id}>
                                                                        {year.year_name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">
                                                                Account <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="default-select form-control wide"
                                                                name="account_id"
                                                                value={formData.account_id}
                                                                onChange={handleInputChange}
                                                                required
                                                            >
                                                                <option value="">Select Account</option>
                                                                {accounts.map((account) => (
                                                                    <option key={account.id} value={account.id}>
                                                                        {account.account_code} - {account.account_name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Branch</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={getBranchName()}
                                                                readOnly
                                                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                                            />
                                                            <input
                                                                type="hidden"
                                                                name="branch_id"
                                                                value={formData.branch_id}
                                                            />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Period Type <span className="text-danger">*</span>
                                                            </label>
                                                            <select
                                                                className="default-select  form-control wide"
                                                                name="period_type"
                                                                value={formData.period_type}
                                                                onChange={handleInputChange}
                                                                required
                                                            >
                                                                <option value="Annual">Annual</option>
                                                                <option value="Quarterly">Quarterly</option>
                                                                <option value="Monthly">Monthly</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Period Name</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                name="period_name"
                                                                value={formData.period_name}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., FY 2024-25, Q1 2024, Jan 2024"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Start Date <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                name="start_date"
                                                                value={formData.start_date}
                                                                onChange={handleInputChange}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                End Date <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                name="end_date"
                                                                value={formData.end_date}
                                                                onChange={handleInputChange}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">
                                                                Budgeted Amount <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                name="budgeted_amount"
                                                                value={formData.budgeted_amount}
                                                                onChange={handleInputChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Warning Threshold (%)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                name="warning_threshold"
                                                                value={formData.warning_threshold}
                                                                onChange={handleInputChange}
                                                            />
                                                            <small className="text-muted">Show warning when budget utilization reaches this %</small>
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Alert Threshold (%)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                name="alert_threshold"
                                                                value={formData.alert_threshold}
                                                                onChange={handleInputChange}
                                                            />
                                                            <small className="text-muted">Show alert when budget utilization reaches this %</small>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <label className="form-label">Remarks</label>
                                                            <textarea
                                                                className="form-control"
                                                                name="remarks"
                                                                value={formData.remarks}
                                                                onChange={handleInputChange}
                                                                rows="2"
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    name="is_active"
                                                                    checked={formData.is_active}
                                                                    onChange={handleInputChange}
                                                                />
                                                                <label className="form-check-label">
                                                                    Active Budget
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-12 text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary me-2"
                                                                onClick={resetForm}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button type="submit" className="btn btn-primary">
                                                                <i className="fa fa-save"></i> {editingBudget ? 'Update' : 'Create'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    )}

                                    {/* Budgets Table */}
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={budgets}
                                            pagination={true}
                                            paginationPageSize={10}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>

                                    {/* Info Panel */}
                                    <div className="alert alert-info mt-4">
                                        <h6><i className="fa fa-info-circle"></i> Budget Management Tips:</h6>
                                        <ul className="mb-0">
                                            <li><strong>Period Types:</strong> Create Annual budgets for yearly planning, Quarterly for seasonal planning, or Monthly for detailed tracking.</li>
                                            <li><strong>Thresholds:</strong> Warning threshold (default 80%) shows yellow alert, Alert threshold (default 100%) shows red alert.</li>
                                            <li><strong>Branch-wise:</strong> Leave branch empty to track budget across all branches, or select specific branch.</li>
                                            <li><strong>Variance:</strong> Positive variance means under budget (good), negative means over budget (needs attention).</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
